import {
    CallSplit,
    CheckCircle,
    Delete,
    Merge,
    RadioButtonUnchecked,
    TableChart,
    TableRows,
} from '@mui/icons-material';
import {
    Box,
    Card,
    CardContent,
    Checkbox,
    Chip,
    LinearProgress,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { ELinks } from '@sharedInterfaces/globalEnums';
import { checkPermissions } from '@sharedInterfaces/IPermissions';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import deleteSkill from '@src/APIs/graphQl/Skill/deleteSkill';
import getSkills from '@src/APIs/graphQl/Skill/getSkills';
import MergeSkillsDialog from '@src/App/Components/Dialogs/MergeSkillsDialog/MergeSkillsDialog';
import SplitSkillsDialog from '@src/App/Components/Dialogs/SplitSkillsDialog/SplitSkillsDialog';
import LoadingBox from '@src/App/Components/General/LoadingBox/LoadingBox';
import SkillOverlay from '@src/App/Components/Overlays/SkillOverlay/SkillOverlay';
import { textContainsLowerCase } from '@src/Objects/Helper';
import type SkillDetail from '@src/Objects/SkillDetail';
import type { AppState } from '@store/store';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import './SkillsPage.css';
import type { CustomGraphQLError } from '@src/APIs/graphQl/graphQL';
import MvpSubNav, { type IMvpQuickAction, type IMvpRole } from '@src/App/Components/Core/MvpSubNav/MvpSubNav';
import { useDialog } from '@src/App/Components/Dialogs/DialogManager';
import SegmentLevelBadge from '@src/App/Components/Items/ItemBadge/SegmentLevelBadge';
import type { IDataTableField } from '@src/App/Components/Tables/DataTable/DataTable';
import { EIconCategory, getAppIcon } from '@src/helper/iconRegistry';

import EntitiesPage, { EViewType, type IEntitiesPageAction } from '../../Generic/EntitiesPage/EntitiesPage';
import type { IViewType } from '../../Generic/EntitiesPage/ViewTypeMenu';

/**
 * Component representing the SkillsPage.
 *
 * @returns {JSX.Element} The JSX element for the SkillsPage.
 */
function SkillsPage() {
    const { openDialog } = useDialog();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).skills;
    const emp = useSelector((state: AppState) => state.employee);
    const levelDefinitions = useSelector((state: AppState) => state.levelDefinition);
    const maxLevel = levelDefinitions.length;
    const permissions = useSelector((state: AppState) => state.permissions);
    const entities = useSelector((state: AppState) => state.skills);

    const [loadingError, setLoadingError] = useState<CustomGraphQLError | null>(null);
    useEffect(() => {
        getSkills().catch((ex) => {
            setLoadingError(ex);
        });
    }, []);

    const [selected, setSelected] = useState<number[]>([]);
    const [activeRole, setActiveRole] = useState('admin');
    const navigate = useNavigate();
    const theme = useTheme();

    const roles: IMvpRole[] = [
        {
            id: 'admin',
            label: 'Admin',
            icon: getAppIcon(EIconCategory.SETTINGS),
        },
        {
            id: 'hr',
            label: 'HR',
            icon: getAppIcon(EIconCategory.EMPLOYEES),
        },
    ];

    const quickActions: IMvpQuickAction[] = [
        {
            id: 'skill-categories',
            label: 'Kategorien',
            icon: getAppIcon(EIconCategory.CATEGORIES),
            onClick: () => navigate(`/${ELinks.SKILLCATEGORIES}`),
            variant: 'primary',
        },
        {
            id: 'skill-manager',
            label: 'Skill Manager',
            icon: getAppIcon(EIconCategory.SKILLS),
            onClick: () => navigate(`/${ELinks.PROFILE}?tab=5`),
            variant: 'default' as const,
        },
    ];

    const tableFields: IDataTableField<any>[] = [
        { id: 'title', label: langStrings.skillName, align: 'left', disablePadding: false, type: 'string' },
        { id: 'employees', label: langStrings.employees, align: 'center', disablePadding: false, type: 'string' },
        {
            id: 'minLevel',
            label: langStrings.lowestEmplyeeSkillLevel,
            align: 'center',
            disablePadding: false,
            type: 'string',
        },
        {
            id: 'averageLevel',
            label: langStrings.averageEmployeeSkillLevel,
            align: 'center',
            disablePadding: false,
            type: 'string',
        },
        {
            id: 'maxLevel',
            label: langStrings.highestEmployeeSkillLevel,
            align: 'center',
            disablePadding: false,
            type: 'string',
        },
        { id: 'products', label: langStrings.products, align: 'center', disablePadding: false, type: 'string' },
    ];

    const types: IViewType<SkillDetail>[] = [
        {
            id: EViewType.TABLE,
            title: langStrings.oftenUsed.table,
            icon: <TableRows fontSize="small" />,
            minWidth: 375,
            renderContainer: (allEntities, innerEntries, width, selectedIds) => {
                const isSelected = (id: number) => selectedIds.indexOf(id) !== -1;
                const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
                    if (event.target.checked) {
                        const newSelecteds = innerEntries.map((n) => n.id);
                        setSelected(newSelecteds);
                        return;
                    }
                    setSelected([]);
                };
                const handleClick = (id: number) => {
                    const selectedIndex = selected.indexOf(id);
                    let newSelected: number[] = [];

                    if (selectedIndex === -1) {
                        newSelected = newSelected.concat(selected, id);
                    } else if (selectedIndex === 0) {
                        newSelected = newSelected.concat(selected.slice(1));
                    } else if (selectedIndex === selected.length - 1) {
                        newSelected = newSelected.concat(selected.slice(0, -1));
                    } else if (selectedIndex > 0) {
                        newSelected = newSelected.concat(
                            selected.slice(0, selectedIndex),
                            selected.slice(selectedIndex + 1),
                        );
                    }
                    setSelected(newSelected);
                };

                return (
                    <Box sx={{ width: '100%' }}>
                        <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
                            <TableContainer>
                                <Table>
                                    <TableHead
                                        sx={{
                                            bgcolor: (theme) =>
                                                theme.palette.mode === 'dark'
                                                    ? alpha(theme.palette.primary.light, 0.15)
                                                    : alpha(theme.palette.primary.main, 0.12),
                                        }}
                                    >
                                        <TableRow>
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    color="primary"
                                                    icon={<RadioButtonUnchecked />}
                                                    checkedIcon={<CheckCircle />}
                                                    indeterminate={
                                                        selected.length > 0 && selected.length < innerEntries.length
                                                    }
                                                    checked={
                                                        innerEntries.length > 0 &&
                                                        selected.length === innerEntries.length
                                                    }
                                                    onChange={handleSelectAllClick}
                                                    inputProps={{
                                                        'aria-label': 'select all skills',
                                                    }}
                                                />
                                            </TableCell>
                                            {tableFields.map((field) => (
                                                <TableCell
                                                    key={field.id}
                                                    align={field.align}
                                                    sx={{
                                                        fontWeight: 500,
                                                        color: (theme) =>
                                                            theme.palette.mode === 'dark'
                                                                ? theme.palette.primary.light
                                                                : theme.palette.primary.dark,
                                                    }}
                                                >
                                                    {field.label}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {innerEntries.map((entity) => {
                                            const isItemSelected = isSelected(entity.id);
                                            const labelId = `enhanced-table-checkbox-${entity.id}`;

                                            return (
                                                <TableRow
                                                    hover
                                                    key={entity.id}
                                                    role="checkbox"
                                                    aria-checked={isItemSelected}
                                                    tabIndex={-1}
                                                    selected={isItemSelected}
                                                    onDoubleClick={() => navigate(`/${ELinks.SKILLS}/${entity.id}`)}
                                                    sx={{ cursor: 'pointer' }}
                                                >
                                                    <TableCell padding="checkbox">
                                                        <Checkbox
                                                            color="primary"
                                                            icon={<RadioButtonUnchecked />}
                                                            checkedIcon={<CheckCircle />}
                                                            checked={isItemSelected}
                                                            onChange={() => handleClick(entity.id)}
                                                            inputProps={{
                                                                'aria-labelledby': labelId,
                                                            }}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <SkillOverlay skillId={entity.id}>
                                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                {entity.title}
                                                            </Typography>
                                                        </SkillOverlay>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Chip
                                                            label={entity.employees.length}
                                                            size="small"
                                                            color="primary"
                                                            variant="outlined"
                                                        />
                                                    </TableCell>
                                                    <TableCell
                                                        align="center"
                                                        sx={{
                                                            bgcolor:
                                                                entity.minLevel > 0
                                                                    ? alpha(
                                                                          theme.palette.levels?.[
                                                                              entity.minLevel as 1 | 2 | 3 | 4 | 5
                                                                          ] || theme.palette.primary.main,
                                                                          0.15,
                                                                      )
                                                                    : 'transparent',
                                                        }}
                                                    >
                                                        {entity.minLevel > 0 ? (
                                                            <Box
                                                                sx={{
                                                                    width: 24,
                                                                    height: 24,
                                                                    borderRadius: '50%',
                                                                    bgcolor:
                                                                        theme.palette.levels?.[
                                                                            entity.minLevel as keyof typeof theme.palette.levels
                                                                        ] || theme.palette.primary.main,
                                                                    color: theme.palette.getContrastText(
                                                                        theme.palette.levels?.[
                                                                            entity.minLevel as keyof typeof theme.palette.levels
                                                                        ] || theme.palette.primary.main,
                                                                    ),
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    fontSize: '0.75rem',
                                                                    fontWeight: 600,
                                                                    mx: 'auto',
                                                                }}
                                                            >
                                                                {entity.minLevel}
                                                            </Box>
                                                        ) : (
                                                            <Typography variant="caption" color="text.secondary">
                                                                -
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell
                                                        align="center"
                                                        sx={{
                                                            bgcolor:
                                                                entity.averageLevel > 0
                                                                    ? alpha(
                                                                          theme.palette.levels?.[
                                                                              Math.round(entity.averageLevel) as
                                                                                  | 1
                                                                                  | 2
                                                                                  | 3
                                                                                  | 4
                                                                                  | 5
                                                                          ] || theme.palette.primary.main,
                                                                          0.15,
                                                                      )
                                                                    : 'transparent',
                                                        }}
                                                    >
                                                        {entity.averageLevel > 0 ? (
                                                            <Box
                                                                sx={{
                                                                    width: 24,
                                                                    height: 24,
                                                                    borderRadius: '50%',
                                                                    bgcolor:
                                                                        theme.palette.levels?.[
                                                                            Math.round(
                                                                                entity.averageLevel,
                                                                            ) as keyof typeof theme.palette.levels
                                                                        ] || theme.palette.primary.main,
                                                                    color: theme.palette.getContrastText(
                                                                        theme.palette.levels?.[
                                                                            Math.round(
                                                                                entity.averageLevel,
                                                                            ) as keyof typeof theme.palette.levels
                                                                        ] || theme.palette.primary.main,
                                                                    ),
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    fontSize: '0.75rem',
                                                                    fontWeight: 600,
                                                                    mx: 'auto',
                                                                    title: `Ø Level ${entity.averageLevel.toFixed(1)}`,
                                                                }}
                                                            >
                                                                {Math.round(entity.averageLevel)}
                                                            </Box>
                                                        ) : (
                                                            <Typography variant="caption" color="text.secondary">
                                                                -
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell
                                                        align="center"
                                                        sx={{
                                                            bgcolor:
                                                                entity.maxLevel > 0
                                                                    ? alpha(
                                                                          theme.palette.levels?.[
                                                                              entity.maxLevel as 1 | 2 | 3 | 4 | 5
                                                                          ] || theme.palette.primary.main,
                                                                          0.15,
                                                                      )
                                                                    : 'transparent',
                                                        }}
                                                    >
                                                        {entity.maxLevel > 0 ? (
                                                            <Box
                                                                sx={{
                                                                    width: 24,
                                                                    height: 24,
                                                                    borderRadius: '50%',
                                                                    bgcolor:
                                                                        theme.palette.levels?.[
                                                                            entity.maxLevel as keyof typeof theme.palette.levels
                                                                        ] || theme.palette.primary.main,
                                                                    color: theme.palette.getContrastText(
                                                                        theme.palette.levels?.[
                                                                            entity.maxLevel as keyof typeof theme.palette.levels
                                                                        ] || theme.palette.primary.main,
                                                                    ),
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    fontSize: '0.75rem',
                                                                    fontWeight: 600,
                                                                    mx: 'auto',
                                                                }}
                                                            >
                                                                {entity.maxLevel}
                                                            </Box>
                                                        ) : (
                                                            <Typography variant="caption" color="text.secondary">
                                                                -
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Chip
                                                            label={entity.products.length}
                                                            size="small"
                                                            color="secondary"
                                                            variant="outlined"
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Box>
                );
            },
        },
        {
            id: EViewType.CARD,
            title: langStrings.oftenUsed.cards,
            icon: <TableChart fontSize="small" />,
            renderContainer: (allEntities, innerEntries) => (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
                        gap: 3,
                        p: 2,
                    }}
                >
                    {innerEntries.map((entity) => (
                        <Card
                            key={entity.id}
                            sx={{
                                cursor: 'pointer',
                                borderRadius: 2,
                                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
                                    borderColor: theme.palette.primary.main,
                                },
                            }}
                            onClick={() => navigate(`/${ELinks.SKILLS}/${entity.id}`)}
                        >
                            <CardContent sx={{ p: 3 }}>
                                <Typography
                                    variant="h6"
                                    sx={{ fontWeight: 600, mb: 2, color: theme.palette.primary.main }}
                                >
                                    {entity.title}
                                </Typography>

                                {entity.description && (
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                            mb: 2,
                                            display: '-webkit-box',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        {entity.description}
                                    </Typography>
                                )}

                                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                                    <Chip
                                        label={`${entity.employees.length} Mitarbeiter`}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                    />
                                    <Chip
                                        label={`${entity.products.length} Produkte`}
                                        size="small"
                                        color="secondary"
                                        variant="outlined"
                                    />
                                </Stack>

                                {entity.employees.length > 0 && (
                                    <Box>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ mb: 1, display: 'block' }}
                                        >
                                            Skill-Level Verteilung:
                                        </Typography>
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    Min:
                                                </Typography>
                                                <SegmentLevelBadge
                                                    level={entity.minLevel}
                                                    title={`Min Level ${entity.minLevel}`}
                                                />
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    Ø:
                                                </Typography>
                                                <SegmentLevelBadge
                                                    level={Math.round(entity.averageLevel)}
                                                    title={`Ø Level ${entity.averageLevel.toFixed(1)}`}
                                                />
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    Max:
                                                </Typography>
                                                <SegmentLevelBadge
                                                    level={entity.maxLevel}
                                                    title={`Max Level ${entity.maxLevel}`}
                                                />
                                            </Box>
                                        </Stack>
                                        <Box sx={{ mt: 1 }}>
                                            <LinearProgress
                                                variant="determinate"
                                                value={(entity.averageLevel / maxLevel) * 100}
                                                sx={{
                                                    height: 4,
                                                    borderRadius: 2,
                                                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                                                    '& .MuiLinearProgress-bar': {
                                                        bgcolor: theme.palette.primary.main,
                                                    },
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    ))}
                </Box>
            ),
        },
    ];

    const actions: IEntitiesPageAction[] = [
        {
            id: 'merge',
            text: langStrings.merge,
            icon: <Merge />,
            filter(selectedCount) {
                if (
                    !checkPermissions('Skills', 'Delete', permissions) ||
                    !checkPermissions('Skills', 'Edit', permissions)
                )
                    return false;
                return selectedCount > 1;
            },
            action(selectedIDs) {
                if (!this.filter(selectedIDs.length)) return;

                openDialog(
                    <MergeSkillsDialog
                        id="MergeSkillsDialog"
                        resolve={() => {
                            setSelected([]);
                        }}
                        skillIds={selected}
                    />,
                );
            },
        },
        {
            id: 'split',
            text: langStrings.split,
            icon: <CallSplit />,
            filter(selectedCount) {
                if (!checkPermissions('Skills', 'Add', permissions) || !checkPermissions('Skills', 'Edit', permissions))
                    return false;
                return selectedCount === 1;
            },
            action(selectedIDs) {
                if (!this.filter(selectedIDs.length)) return;
                openDialog(
                    <SplitSkillsDialog
                        id="SplitSkillsDialog"
                        resolve={() => {
                            setSelected([]);
                        }}
                        skillsWithoutFilter={entities}
                        skill={entities.find((s) => selected.indexOf(s.id) > -1) as SkillDetail}
                    />,
                );
            },
        },
        {
            id: 'delete',
            text: langStrings.delete,
            icon: <Delete />,
            filter(selectedCount) {
                if (!checkPermissions('Skills', 'Delete', permissions)) return false;
                return selectedCount > 0;
            },
            action(selectedIDs) {
                if (!this.filter(selectedIDs.length)) return;

                const skillsToDelete = entities.filter((s) => selected.includes(s.id));
                const skillNames = skillsToDelete.map((s) => s.title).join(', ');

                if (window.confirm(`Möchten Sie die folgenden Skills wirklich löschen?\n\n${skillNames}`)) {
                    Promise.all(selected.map((id) => deleteSkill(id)))
                        .then(() => {
                            setSelected([]);
                        })
                        .catch((error) => {
                            console.error('Error deleting skills:', error);
                        });
                }
            },
        },
    ];

    if (!maxLevel) return <LoadingBox />;
    return (
        <>
            <MvpSubNav
                currentPageTitle="Skills"
                roles={roles}
                activeRole={activeRole}
                onRoleChange={setActiveRole}
                quickActions={quickActions}
            />
            <EntitiesPage
                title={langStrings.skills}
                entities={entities}
                selected={selected}
                loadingError={loadingError}
                filter={(entity, selectedView, searchText) =>
                    selectedView.filter(entity) && textContainsLowerCase(searchText, entity.title)
                }
                setSelected={setSelected}
                views={[
                    {
                        id: 'all',
                        title: langStrings.oftenUsed.allEntries,
                        filter() {
                            return true;
                        },
                    },
                    {
                        id: 'my',
                        title: langStrings.oftenUsed.myEntries,
                        filter(entity) {
                            return entity.employees.find((e) => e.id === emp?.id) !== undefined;
                        },
                    },
                ]}
                types={types}
                actions={actions}
            />
        </>
    );
}

export default SkillsPage;
